import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useConnectCalendarMutation } from '../api/preCallApi';
import Swal from 'sweetalert2';
import { Loader2 } from 'lucide-react';

export function PreCallCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [connectCalendar] = useConnectCalendarMutation();
  const calledRef = useRef(false);

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const provider = state || searchParams.get('provider') || 'google';

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!code) {
      Swal.fire({
        icon: 'error',
        title: 'Authorization Failed',
        text: 'No authorization code returned from calendar provider.',
      }).then(() => {
        navigate('/admin/pre-call');
      });
      return;
    }

    const exchangeCode = async () => {
      try {
        await connectCalendar({ provider, code }).unwrap();
        
        Swal.fire({
          icon: 'success',
          title: 'Account Linked!',
          text: `Your ${provider === 'google' ? 'Google Calendar' : 'Outlook'} account was connected successfully.`,
          timer: 3000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
        });
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Connection Failed',
          text: err?.data?.message || 'Could not verify calendar connection.',
        });
      } finally {
        navigate('/admin/pre-call');
      }
    };

    exchangeCode();
  }, [code, provider, connectCalendar, navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '450px',
      gap: '24px',
      color: 'var(--admin-card-text)',
      fontFamily: 'var(--font-body, "Space Grotesk", sans-serif)'
    }}>
      <div style={{
        padding: '40px 32px',
        borderRadius: '16px',
        backgroundColor: 'var(--admin-card-bg)',
        border: '1px solid var(--admin-card-border)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: '420px',
        width: '100%',
        margin: '0 auto'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'rgba(56, 189, 248, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <Loader2 className="animate-spin" size={32} color="#38bdf8" />
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 8px 0', letterSpacing: '-0.02em', fontFamily: "'Space Grotesk', sans-serif" }}>
          Linking {provider === 'google' ? 'Google' : 'Microsoft'} Calendar
        </h2>
        <p style={{ color: 'var(--admin-card-subtext)', fontSize: '13px', margin: 0, lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
          Please wait a moment while we establish a secure OAuth connection and import your upcoming meetings.
        </p>
      </div>
    </div>
  );
}
